// oxlint-disable typescript/no-unnecessary-type-assertion
import {
  MjmlAll,
  MjmlAttributes,
  MjmlBody,
  MjmlClass,
  MjmlDivider,
  MjmlHead,
  MjmlStyle,
  MjmlText,
} from "@faire/mjml-react";
import ReactDOMServer from "react-dom/server";
import { notEmpty } from "../../../../../utils/not-empty";
import { makeRenderPaddingPxs } from "../../../../email-design/util";

import type { ButtonTemplateSettings } from "../../../../email-design/types/email-button-settings";
import type { EmailDesignSchema } from "../../../../email-design/types/email-design-type";
import type { IMjmlClassProps } from "@faire/mjml-react";

export function convertSettingsToMjml(
  settings: Partial<EmailDesignSchema>,
  isStyleableColumns: boolean,
): string {
  const fontFamily = [settings.text_settings?.font, settings.text_settings?.fallback_font].filter(
    notEmpty,
  );
  const headerFontFamily = [
    settings.text_settings?.header_font,
    settings.text_settings?.header_fallback_font,
    ...fontFamily,
  ].filter(notEmpty);

  const styles = {
    h1: [
      maybeInclude("font-size", (val) => `${val}px`, settings.text_settings?.heading1?.font_size),
      maybeInclude("color", (val) => val, settings.text_settings?.heading1?.font_colour),
      maybeInclude(
        "font-weight",
        (val) => (val ? "bold" : "normal"),
        settings.text_settings?.heading1?.bold,
      ),
      maybeInclude("font-family", (val) => val, headerFontFamily.join(", ")),
    ],
    h2: [
      maybeInclude("font-size", (val) => `${val}px`, settings.text_settings?.heading2?.font_size),
      maybeInclude("color", (val) => val, settings.text_settings?.heading2?.font_colour),
      maybeInclude(
        "font-weight",
        (val) => (val ? "bold" : "normal"),
        settings.text_settings?.heading2?.bold,
      ),
      maybeInclude("font-family", (val) => val, headerFontFamily.join(", ")),
    ],
    h3: [
      maybeInclude("font-size", (val) => `${val}px`, settings.text_settings?.heading3?.font_size),
      maybeInclude("color", (val) => val, settings.text_settings?.heading3?.font_colour),
      maybeInclude(
        "font-weight",
        (val) => (val ? "bold" : "normal"),
        settings.text_settings?.heading3?.bold,
      ),
      maybeInclude("font-family", (val) => val, headerFontFamily.join(", ")),
    ],
    h4: [
      maybeInclude("font-size", (val) => `${val}px`, settings.text_settings?.heading4?.font_size),
      maybeInclude("color", (val) => val, settings.text_settings?.heading4?.font_colour),
      maybeInclude(
        "font-weight",
        (val) => (val ? "bold" : "normal"),
        settings.text_settings?.heading4?.bold,
      ),
      maybeInclude("font-family", (val) => val, headerFontFamily.join(", ")),
    ],
    paper: [maybeInclude("background-color", (val) => val, settings.layout_settings?.paper_colour)],
    paperBorder: [
      maybeInclude(
        "border",
        (val) =>
          val.width === 0
            ? "none"
            : `${val.width}px solid ${val.colour}
      `,
        settings.layout_settings?.border,
      ),
    ],
    header: [
      maybeInclude("background-color", (val) => val, settings.header?.backgroundColor),
      maybeInclude(
        "width",
        (val) => `${val}px`,
        settings.header?.fullWidth ? undefined : settings.layout_settings?.max_paper_width,
      ),
      maybeInclude("font-family", (val) => val, headerFontFamily.join(", ")),
    ],
    outlook: {
      paper: [
        maybeInclude("background-color", (val) => val, settings.layout_settings?.paper_colour),
        maybeInclude("max-width", (val) => val, `${settings.layout_settings?.max_paper_width}px`),
      ],
      header: [
        maybeInclude("background-color", (val) => val, settings.header?.backgroundColor),
        maybeInclude(
          "max-width",
          (val) => `${val}px`,
          settings.header?.fullWidth ? undefined : settings.layout_settings?.max_paper_width,
        ),
      ],
      footer: [
        maybeInclude("background-color", (val) => val, settings.layout_settings?.paper_colour),
        maybeInclude("max-width", (val) => val, `${settings.layout_settings?.max_paper_width}px`),
      ],
    },
  };

  const paddings = makeRenderPaddingPxs(settings.layout_settings);

  const data = ReactDOMServer.renderToString(
    <MjmlHead>
      <MjmlAttributes>
        <MjmlAll
          font-family={fontFamily.join(", ")}
          ico-font-family={fontFamily.join(", ")}
          font-size={settings.text_settings?.font_size}
          color={settings.text_settings?.font_colour}
          line-height={
            settings.text_settings?.line_height
              ? `${settings.text_settings?.line_height}`
              : undefined
          }
        />
        <MjmlBody
          background-color={settings.layout_settings?.background_colour}
          width={settings.layout_settings?.max_paper_width}
        />
        {settings.layout_settings?.paragraph_padding !== undefined && (
          <>
            <MjmlText padding={`0 0 ${settings.layout_settings?.paragraph_padding}px 0`} />
            <MjmlDivider padding={`0 0 ${settings.layout_settings?.paragraph_padding}px 0`} />
          </>
        )}
        <MjmlClass name="last" padding="0" />

        {Boolean(settings.header) && (
          <MjmlClass
            name="header"
            {...(settings!.header!.fullWidth ? { fullWidth: "full-width" } : {})}
            padding-left={`${settings!.header!.paper_padding_side}px`}
            padding-right={`${settings!.header!.paper_padding_side}px`}
            padding-top={`${settings!.header!.paper_padding_top}px`}
            padding-bottom={`${settings!.header!.paper_padding_bottom}px`}
          />
        )}

        {Boolean(settings.layout_settings) && (
          <>
            <MjmlClass
              name="paper"
              padding-left={`${settings!.layout_settings!.paper_padding_side}px`}
              padding-right={`${settings.layout_settings!.paper_padding_side}px`}
              padding-top={`${settings!.layout_settings!.paper_padding_top}px`}
              padding-bottom={`${settings!.layout_settings!.paper_padding_bottom}px`}
            />
          </>
        )}
        {Boolean(settings.footer_layout_settings) && (
          <MjmlClass
            name="footer"
            padding-left={`${settings!.footer_layout_settings!.paper_padding_side}px`}
            padding-right={`${settings!.footer_layout_settings!.paper_padding_side}px`}
            padding-bottom={`${settings!.footer_layout_settings!.paper_padding_bottom}px`}
            padding-top={`${settings!.footer_layout_settings!.paper_padding_top}px`}
          />
        )}
        {settings.button_settings?.primary_button && (
          <MjmlClass
            name="primary_button"
            {...buttonProps(
              settings.button_settings.primary_button,
              settings.layout_settings?.paragraph_padding,
            )}
          />
        )}
        {settings.button_settings?.secondary_button && (
          <MjmlClass
            name="secondary_button"
            {...buttonProps(
              settings.button_settings.secondary_button,
              settings.layout_settings?.paragraph_padding,
            )}
          />
        )}
        {settings.divider_settings?.primary && (
          <MjmlClass
            name="primary_divider"
            borderWidth={`${settings.divider_settings.primary.width}px`}
            borderColor={settings.divider_settings.primary.color}
            borderStyle={settings.divider_settings.primary.style}
          />
        )}
        {settings.divider_settings?.secondary && (
          <MjmlClass
            name="secondary_divider"
            borderWidth={`${settings.divider_settings.secondary.width}px`}
            borderColor={settings.divider_settings.secondary.color}
            borderStyle={settings.divider_settings.secondary.style}
          />
        )}
        <MjmlClass name="section" padding="0" margin="0" />
        {/* This is for backwards compatibility. If an email design is saved and a
        moment is not re-saved, then as the moment was meant to provide padding directly on the column,
        that will be missing, so it'll revert to using class which is still correct insofar that the
        template defines what ALL the columns are rather than the individual columns */}
        <MjmlClass
          name="column"
          {...(isStyleableColumns
            ? {
                "padding-left": paddings.paddingLeft,
                "padding-right": paddings.paddingRight,
                "padding-top": paddings.paddingTop,
                "padding-bottom": paddings.paddingBottom,
              }
            : {
                padding: `${
                  settings.layout_settings?.column_spacing_type === "padding"
                    ? settings.layout_settings.column_padding
                    : 0
                }px`,
                innerBorder: `solid ${settings.layout_settings?.paper_colour} ${
                  settings.layout_settings?.column_spacing_type === "spacing"
                    ? settings.layout_settings.column_spacing
                    : 0
                }px`,
              })}
        />
      </MjmlAttributes>
      <MjmlStyle inline>
        {`
          h1 {
            ${styles.h1.filter(notEmpty).join(";\n")};
          }
          h2 {
            ${styles.h2.filter(notEmpty).join(";\n")};
          }
          h3 {
            ${styles.h3.filter(notEmpty).join(";\n")};
          }
          h4 {
            ${styles.h4.filter(notEmpty).join(";\n")};
          }
          .paper {
            ${styles.paper.filter(notEmpty).join(";\n")};
          }
          .paper > table {
            ${styles.outlook.paper.filter(notEmpty).join(";\n")};
            ${styles.paperBorder.filter(notEmpty).join(";\n")};
          }
          .header {
            ${styles.header.filter(notEmpty).join(";\n")};
          }
          .header > table {
            ${styles.outlook.header.filter(notEmpty).join(";\n")};
          }
          .footer > table {
            ${styles.outlook.footer.filter(notEmpty).join(";\n")};
          }
          h1, h2, h3, h4, p, ul, ol {
            margin: 0;
          }
          ol, ul {
            list-style-position: inside;
          }
          ol.nestedlist_1 {
            list-style-type: upper-alpha;
          }
          ol.nestedlist_2 {
            list-style-type: lower-alpha;
          }
          ol.nestedlist_4 {
            list-style-type: upper-roman;
          }
          ol.nestedlist_4 {
            list-style-type: lower-roman;
          }
          .btn-full-width a { display: block !important; }
          a {
            text-decoration: none;
            color: ${settings.text_settings?.link_color ?? "inherit"};
          }
          a span {
            text-decoration: underline;
          }
        `}
      </MjmlStyle>
    </MjmlHead>,
  );
  return data;
}

function maybeInclude<T>(key: string, valueFormat: (val: T) => string, value?: T): string | null {
  return value !== undefined ? `${key}: ${valueFormat(value)}` : null;
}

function buttonProps(
  buttonSettings: ButtonTemplateSettings,
  paragraphPadding?: number,
): IMjmlClassProps {
  return {
    padding: `0 0 ${paragraphPadding ?? 0}px 0`,
    color: buttonSettings.color,
    backgroundColor: buttonSettings.background_color,
    fontSize: `${buttonSettings.font_size}px`,
    borderRadius: `${buttonSettings.border_radius}px`,
    border:
      buttonSettings.border && buttonSettings.color
        ? `${buttonSettings.border}px solid ${buttonSettings.color}`
        : undefined,
  };
}
